<template>
  <div>
    <PageHeader :title="t('title')">
      <button
        v-if="selectedProfiles.length > 0"
        class="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-xs hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="isSendingInvitations"
        @click="sendBulkInvitations"
      >
        {{ t("send_invitations", { count: selectedProfiles.length }) }}
      </button>
      <CreateButton @click="createProfileModalOpen = true">{{ t("new_user") }}</CreateButton>
    </PageHeader>
    <MerchantProfileCreateModal
      :open="createProfileModalOpen"
      :organizations="availableFilters.organizations"
      @close="createProfileModalOpen = false"
      @success="profileCreated"
    />
    <SearchToolbar
      class="mb-6"
      placeholder="Rechercher par nom ou email"
      :available-filters="availableFilters"
      :selected-filters="selectedFilters"
      :initial-keywords="keywords"
      @update-filter-search="updateFilterSearch"
      @update-keyword-search="updateKeywordSearch"
    />
    <MerchantProfilesList
      :page="page"
      :keywords="keywords"
      :organization-ids="selectedFilters.organizations"
      :store-ids="selectedFilters.stores"
      :roles="selectedFilters.roles"
      :signed-up="signedUpFilter"
      @selected-rows="(rows) => (selectedProfiles = rows)"
    />
  </div>
</template>

<script>
import { CreateButton, PageHeader, SearchToolbar } from "@estaly/ui";
import { getQueryParams, setQueryParams } from "@estaly/ui/src/utils/query_params";
import MerchantProfilesList from "@/components/merchant_profiles/MerchantProfilesList.vue";
import MerchantProfileCreateModal from "@/components/merchant_profiles/MerchantProfileCreateModal.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import { useI18n } from "vue-i18n";

export default {
  components: {
    CreateButton,
    PageHeader,
    SearchToolbar,
    MerchantProfilesList,
    MerchantProfileCreateModal,
  },
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      page: getQueryParams().page || 1,
      keywords: getQueryParams().keywords || null,
      selectedProfiles: [],
      isSendingInvitations: false,
      createProfileModalOpen: false,
      availableFilters: {
        organizations: [],
        stores: [],
        roles: [],
        signedUp: [],
      },
      selectedFilters: {
        organizations: [],
        stores: [],
        roles: [],
        signedUp: [],
      },
    };
  },
  computed: {
    ...mapState("merchantProfiles", ["currentPage"]),
    ...mapGetters("merchantProfiles", ["roles", "signedUpStatuses"]),
    ...mapState("stores", { stores: "index" }),
    ...mapState("organizations", { organizations: "index" }),

    signedUpFilter() {
      if (this.selectedFilters.signedUp.length !== 1) return null;
      return this.selectedFilters.signedUp[0];
    },
  },
  watch: {
    currentPage() {
      setQueryParams({ page: this.currentPage, keywords: this.keywords });
    },
    keywords() {
      setQueryParams({ page: this.currentPage, keywords: this.keywords });
    },
  },
  async created() {
    this.availableFilters.roles = this.roles().map((role) => ({
      label: this.$t(`roles.${role}`),
      value: role,
    }));
    this.availableFilters.signedUp = this.signedUpStatuses();

    await Promise.all([
      this.fetchStores({ perPage: 2000, status: ["active", "inactive"] }),
      this.fetchOrganizations({ perPage: 2000 }),
    ]);

    this.availableFilters.stores = this.stores
      .filter((store) => store.salesChannel !== null)
      .map((store) => ({ label: store.salesChannel, value: store.id }));

    this.availableFilters.organizations = this.organizations.map((organization) => ({
      label: organization.name,
      value: organization.id,
    }));
  },
  methods: {
    ...mapActions("stores", { fetchStores: "fetchIndex" }),
    ...mapActions("organizations", { fetchOrganizations: "fetchIndex" }),
    ...mapActions("merchantProfiles", ["fetchIndex", "sendBulkInvitation"]),
    ...mapActions("notifications", ["notify"]),

    updateFilterSearch(filterName, filterOptions) {
      this.selectedFilters[filterName] = filterOptions;
      this.page = 1;
    },
    updateKeywordSearch(keywords) {
      this.keywords = keywords;
      this.page = 1;
    },
    fetchProfiles() {
      return this.fetchIndex({
        page: this.currentPage,
        keywords: this.keywords,
        organizationIds: this.selectedFilters.organizations,
        storeIds: this.selectedFilters.stores,
        roles: this.selectedFilters.roles,
        signedUp: this.signedUpFilter,
      });
    },
    async profileCreated() {
      this.createProfileModalOpen = false;
      this.notify({
        category: "simple",
        type: "success",
        title: this.t("profile_created"),
      });
      await this.fetchProfiles();
    },
    async sendBulkInvitations() {
      const count = this.selectedProfiles.length;
      if (!confirm(this.t("confirm_send_invitations", { count }))) return;

      this.isSendingInvitations = true;
      const ids = this.selectedProfiles.map((p) => p.id);
      const result = await this.sendBulkInvitation(ids);

      if (result) {
        this.notify({
          category: "simple",
          type: "success",
          title: this.t("invitations_sent", { count: ids.length }),
        });
        await this.fetchProfiles();
      }

      this.isSendingInvitations = false;
    },
  },
};
</script>

<i18n src="./MerchantProfilesIndex.i18n.json"></i18n>
